import React from 'react';
import { Settings, Phone, Mail, Globe, Upload } from 'lucide-react';

const formatFileSize = (bytes = 0) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.max(1, Math.round(kb))} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
};

const DetailsStep = ({
    formData,
    errors,
    handleInputChange,
    handleArrayChange,
    onImageUpload,
    imageUploadState,
    amenityOptions,
    accessibilityOptions
}) => {
    return (
        <div className="form-section">
            <h3>
                <Settings size={24} />
                Details & Amenities
            </h3>
            <p>Add contact information, photos, and list the features your venue offers.</p>

            <div className="form-grid">
                <div className="form-field required">
                    <label>Phone</label>
                    <div className="input-with-icon">
                        <Phone size={16} className="input-icon" />
                        <input
                            type="tel"
                            className={`wizard-input ${errors.phone ? 'error' : ''}`}
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            style={{ paddingLeft: '44px' }}
                            placeholder="+1 (555) 000-0000"
                        />
                    </div>
                    {errors.phone && <div className="error-message">{errors.phone}</div>}
                </div>

                <div className="form-field required">
                    <label>Email</label>
                    <div className="input-with-icon">
                        <Mail size={16} className="input-icon" />
                        <input
                            type="email"
                            className={`wizard-input ${errors.email ? 'error' : ''}`}
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            style={{ paddingLeft: '44px' }}
                            placeholder="venue@example.com"
                        />
                    </div>
                    {errors.email && <div className="error-message">{errors.email}</div>}
                </div>

                <div className="form-field">
                    <label>Website</label>
                    <div className="input-with-icon">
                        <Globe size={16} className="input-icon" />
                        <input
                            type="url"
                            className={`wizard-input ${errors.website ? 'error' : ''}`}
                            value={formData.website}
                            onChange={(e) => handleInputChange('website', e.target.value)}
                            style={{ paddingLeft: '44px' }}
                            placeholder="https://example.com"
                        />
                    </div>
                    {errors.website && <div className="error-message">{errors.website}</div>}
                </div>
            </div>

            <div className="form-field" style={{ marginTop: '24px' }}>
                <label>Venue Image</label>
                <div className="upload-tile">
                    <input
                        type="file"
                        accept="image/png, image/jpeg, image/webp, image/gif"
                        onChange={(e) => onImageUpload(e.target.files?.[0] || null)}
                        disabled={imageUploadState?.isUploading}
                    />

                    <div className="upload-row">
                        <div className="upload-icon" aria-hidden="true"><Upload size={24} /></div>
                        <div>
                            <div className="upload-meta">Upload a venue photo</div>
                            <div className="upload-subtle">Showcase the exterior or main hall. Click to browse.</div>
                        </div>
                    </div>

                    <div className="upload-pills">
                        <span className="upload-pill">JPG / PNG / WEBP</span>
                        <span className="upload-pill">Max 5MB</span>
                    </div>

                    {imageUploadState?.fileName && (
                        <div className="upload-row" style={{ marginTop: '0.35rem' }}>
                            <div className={`file-chip ${imageUploadState?.error ? 'error' : ''}`}>
                                <span className="name">{imageUploadState.fileName}</span>
                                {imageUploadState?.fileSize ? (
                                    <span className="size">• {formatFileSize(imageUploadState.fileSize)}</span>
                                ) : null}
                            </div>
                        </div>
                    )}
                </div>

                {(imageUploadState?.previewUrl || formData.imageUrl) && (
                    <div className="image-preview">
                        <img
                            src={imageUploadState?.previewUrl || formData.imageUrl}
                            alt="Venue preview"
                        />
                    </div>
                )}

                {imageUploadState?.isUploading && (
                    <div className="upload-status">
                        <span>⏳ Uploading image...</span>
                    </div>
                )}

                {imageUploadState?.error && (
                    <div className="upload-status error">
                        <span>⚠ {imageUploadState.error}</span>
                    </div>
                )}
            </div>

            <h4 className="section-subheading">Features & Amenities</h4>
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                <div className="featured-pin">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#fff' }}>
                        <input
                            type="checkbox"
                            checked={formData.hasBalcony}
                            onChange={(e) => handleInputChange('hasBalcony', e.target.checked)}
                        />
                        Has Balcony
                    </label>
                </div>
                <div className="featured-pin">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#fff' }}>
                        <input
                            type="checkbox"
                            checked={formData.hasVipSection}
                            onChange={(e) => handleInputChange('hasVipSection', e.target.checked)}
                        />
                        Has VIP Section
                    </label>
                </div>
            </div>

            <div style={{ marginTop: '16px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)' }}>Amenities</label>
                <div className="chart-options-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                    {amenityOptions.map(option => (
                        <label key={option} className={`chart-option-card ${formData.amenities.includes(option) ? 'selected' : ''}`} style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '8px', minHeight: 'auto' }}>
                            <input
                                type="checkbox"
                                checked={formData.amenities.includes(option)}
                                onChange={(e) => handleArrayChange('amenities', option, e.target.checked)}
                                style={{ display: 'none' }}
                            />
                            <div style={{ fontSize: '0.9rem' }}>{option}</div>
                        </label>
                    ))}
                </div>
            </div>

            <div style={{ marginTop: '24px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255,255,255,0.7)' }}>Accessibility</label>
                <div className="chart-options-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
                    {accessibilityOptions.map(option => (
                        <label key={option} className={`chart-option-card ${formData.accessibilityFeatures.includes(option) ? 'selected' : ''}`} style={{ padding: '12px', display: 'flex', alignItems: 'center', gap: '8px', minHeight: 'auto' }}>
                            <input
                                type="checkbox"
                                checked={formData.accessibilityFeatures.includes(option)}
                                onChange={(e) => handleArrayChange('accessibilityFeatures', option, e.target.checked)}
                                style={{ display: 'none' }}
                            />
                            <div style={{ fontSize: '0.9rem' }}>{option}</div>
                        </label>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DetailsStep;
